import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { IconPin } from '@tabler/icons-react'
import { isEmpty, sortBy } from 'lodash'
import queryString from 'query-string'

import { SearchableDropdown } from '@/components/dropdowns/SearchableDropdown'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { PageHeader } from '@/components/headers/PageHeader'
import { Menu } from '@/components/navigation/Menu'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { Loader } from '@/components/loaders/Loader'
import NoData from '@/components/NoData'
import { toast } from '@/components/toasts/toast.helpers'
import { UserInitial } from '@/components/UserInitial'
import { hasKodiEntitlement } from '@/reducers/auth/currentUser.redux'
import {
  deleteReportChart,
  getReportChartData,
  listPublicReportCharts,
  listReportCharts,
  refreshReportChart,
  renameReportChart,
  setReportChartVisibility,
} from '@/views/Kodi/api/kodiReportsApi'

import { KodiReportCard } from './KodiReportCard'
import { KodiReportEditModal } from './KodiReportEditModal'
import {
  PINNED_BY_QUERY_PARAM,
  REFRESH_COOLDOWN_SECONDS,
  REPORT_CHART_QUERY_PARAM,
  REPORT_TABS,
  REPORT_VISIBILITY,
  SLOT_STATUSES,
  pinnedByStorageKey,
} from './kodiReports.constants'

import './KodiReportsPage.scss'

const TOO_MANY_REQUESTS = 429

const initialCardData = (cards) => {
  return cards.reduce((accumulator, card) => {
    accumulator[card.card_id] = {
      status: SLOT_STATUSES.IDLE,
      chart: null,
      summary: null,
      error: null,
    }
    return accumulator
  }, {})
}

const resultToCardData = (response) => ({
  status: SLOT_STATUSES.READY,
  chart: response.chart,
  summary: response.summary,
  error: null,
  refreshedAt: response.refreshed_at,
  nextRefreshAt: response.next_refresh_at,
  refreshError: response.refresh_error,
})

// The "Pinned by" filter's options: everyone who pinned one of the charts, with
// how many each pinned — the viewer first as "Me", then by name. Only people who
// have charts are offered, so no choice filters the page down to nothing.
const pinnerOptions = (cards, viewerId) => {
  const pinners = {}
  cards.forEach(({ pinned_by: pinnedBy }) => {
    if (!pinnedBy) {
      return
    }
    pinners[pinnedBy.user_id] = pinners[pinnedBy.user_id] || { ...pinnedBy, count: 0 }
    pinners[pinnedBy.user_id].count += 1
  })
  const ordered = sortBy(Object.values(pinners), [
    (pinner) => pinner.user_id !== viewerId,
    (pinner) => pinner.name.toLowerCase(),
  ])
  return ordered.map((pinner) => {
    const label = pinner.user_id === viewerId ? 'Me' : pinner.name
    return {
      value: pinner.user_id,
      label,
      meta: pinner.count,
      labelNode: (
        <span className="flex-align-center small-gap">
          <UserInitial userFullName={pinner.name} index={pinner.user_id} small />
          {label}
        </span>
      ),
    }
  })
}

// "7,8" → [7, 8]. Anything that isn't a user id (a hand-edited URL, a mangled
// stored value) is dropped.
const parsePinnedByIds = (value) => {
  if (typeof value !== 'string') {
    return []
  }
  return value
    .split(',')
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0)
}

// Storage can throw (private browsing, blocked site data). The choice is then
// just not remembered; the URL still carries it.
const readRememberedPinnedBy = (key) => {
  try {
    return parsePinnedByIds(localStorage.getItem(key))
  } catch (error) {
    return []
  }
}

const rememberPinnedBy = (key, ids) => {
  try {
    if (isEmpty(ids)) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, ids.join(','))
    }
  } catch (error) {
    // Not remembered for next time, which nothing else depends on.
  }
}

// Who loses the chart when it's removed, by its visibility.
const REMOVAL_REACH = {
  [REPORT_VISIBILITY.PRIVATE]: '.',
  [REPORT_VISIBILITY.SHARED]: ' for everyone it is shared with.',
  [REPORT_VISIBILITY.ORG]: ' for everyone in your organization.',
}

// A chart someone shared with the viewer only leaves their page; their own is
// removed for everyone who sees it.
const removalMessage = (card) => {
  if (card?.can_edit === false) {
    return `will be removed from your Kodi Reports. ${card.pinned_by?.name || 'Its creator'} still has it.`
  }
  const reach = REMOVAL_REACH[card?.visibility] ?? REMOVAL_REACH[REPORT_VISIBILITY.ORG]
  return `will be removed from Kodi Reports${reach}`
}

// Only a chart's creator changes who sees it, so they're who the toast speaks to.
const sharingToast = (chart) => {
  if (chart.visibility === REPORT_VISIBILITY.ORG) {
    return 'Shared with your organization'
  }
  if (chart.visibility === REPORT_VISIBILITY.SHARED) {
    const { shared_with: sharedWith = [] } = chart
    const count = sharedWith.length
    return `Shared with ${count} ${count === 1 ? 'person' : 'people'}`
  }
  return 'Only you can see this chart now'
}

const KodiReportsPageComponent = ({ flags = {} }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const currentUser = useSelector((state) => state.currentUser)
  const { organizationid } = currentUser
  const [cards, setCards] = useState([])
  const [cardData, setCardData] = useState({})
  const [listStatus, setListStatus] = useState(SLOT_STATUSES.IDLE)
  const [listError, setListError] = useState(null)
  const [editingCardId, setEditingCardId] = useState(null)
  const [savingCardId, setSavingCardId] = useState(null)
  const [removingCardId, setRemovingCardId] = useState(null)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [tab, setTab] = useState(REPORT_TABS.MINE)
  // Public reports are read-only: their pinner manages them from My reports.
  const isPublic = tab === REPORT_TABS.PUBLIC
  // For saves that resolve after a tab switch.
  const tabRef = useRef(tab)
  useEffect(() => {
    tabRef.current = tab
  }, [tab])

  // The "Pinned by" filter lives in the URL; the viewer's last choice is also
  // remembered, to restore when they come back without one.
  const searchParams = queryString.parse(location.search)
  const hasPinnedByParam = PINNED_BY_QUERY_PARAM in searchParams
  const pinnedByIds = parsePinnedByIds(searchParams[PINNED_BY_QUERY_PARAM])
  const storageKey = pinnedByStorageKey(organizationid, currentUser.user_id)

  const replacePinnedByParam = (ids) => {
    const params = queryString.parse(location.search)
    params[PINNED_BY_QUERY_PARAM] = isEmpty(ids) ? undefined : ids.join(',')
    history.replace({ pathname: location.pathname, search: queryString.stringify(params) })
  }

  const handlePinnedByChange = (ids) => {
    rememberPinnedBy(storageKey, ids)
    replacePinnedByParam(ids)
  }

  const pinners = pinnerOptions(cards, currentUser.user_id)
  // A link can name someone with no charts here; they're ignored rather than
  // filtering the page down to nothing.
  const activePinnedByIds = pinnedByIds.filter((id) => {
    return pinners.some((pinner) => pinner.value === id)
  })
  const visibleCards = isEmpty(activePinnedByIds)
    ? cards
    : cards.filter((card) => activePinnedByIds.includes(card.pinned_by?.user_id))

  // Same gate as starting a Kodi session: org 1 has no charts of its own, and
  // the backend refuses both it and anyone without the Kodi entitlement. Nothing
  // loads until it passes, so flags still loading or a user about to be sent
  // away never fetch the org's charts.
  const flagsLoaded = !isEmpty(flags)
  const hasAccess =
    flagsLoaded &&
    Boolean(flags.baltoCoworkEnabled) &&
    organizationid !== 1 &&
    hasKodiEntitlement(currentUser)

  useEffect(() => {
    if (flagsLoaded && !hasAccess) {
      history.push('/')
    }
  }, [flagsLoaded, hasAccess, history])

  // Arriving without a filter in the URL (from the sidebar, say) brings back the
  // viewer's last choice, written into the URL so it behaves like any other.
  useEffect(() => {
    if (!hasAccess || hasPinnedByParam) {
      return
    }
    const remembered = readRememberedPinnedBy(storageKey)
    if (!isEmpty(remembered)) {
      replacePinnedByParam(remembered)
    }
  }, [hasAccess, hasPinnedByParam, storageKey])

  // Per-card data fetches can resolve after the page unmounts; this guards
  // their setState so they don't warn or write to a dead component.
  const mountedRef = useRef(true)
  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const setCardState = useCallback((cardId, partial) => {
    setCardData((previous) => {
      return {
        ...previous,
        [cardId]: { ...(previous[cardId] || {}), ...partial },
      }
    })
  }, [])

  // The backend serves each chart's saved result and only re-runs one that is
  // over a day old, so loading the page doesn't re-query every chart.
  const loadCardData = useCallback(
    async (cardId) => {
      setCardState(cardId, { status: SLOT_STATUSES.LOADING, error: null })
      try {
        const response = await getReportChartData(dispatch, cardId)
        if (!mountedRef.current) {
          return
        }
        setCardState(cardId, resultToCardData(response))
      } catch (error) {
        if (!mountedRef.current) {
          return
        }
        setCardState(cardId, {
          status: SLOT_STATUSES.ERROR,
          error: error?.message || 'Failed to load chart',
        })
      }
    },
    [dispatch, setCardState]
  )

  useEffect(() => {
    if (!hasAccess) {
      return undefined
    }
    let cancelled = false
    const listCharts = isPublic ? listPublicReportCharts : listReportCharts
    const loadCards = async () => {
      setCards([])
      setListStatus(SLOT_STATUSES.LOADING)
      setListError(null)
      try {
        const response = await listCharts(dispatch)
        if (cancelled) {
          return
        }
        setCards(response)
        setCardData(initialCardData(response))
        setListStatus(SLOT_STATUSES.READY)
        response.forEach((card) => {
          loadCardData(card.card_id)
        })
      } catch (error) {
        if (cancelled) {
          return
        }
        setListStatus(SLOT_STATUSES.ERROR)
        setListError(error?.message || 'Failed to load Kodi Reports')
      }
    }
    loadCards()
    return () => {
      cancelled = true
    }
  }, [hasAccess, isPublic, dispatch, loadCardData])

  // A chart that failed to load has nothing on screen to keep, so it reloads
  // from scratch; one that's showing stays up while the refresh runs.
  const handleRefresh = async (cardId) => {
    const previous = cardData[cardId]
    if (previous.status === SLOT_STATUSES.ERROR) {
      loadCardData(cardId)
      return
    }
    setCardState(cardId, { status: SLOT_STATUSES.REFRESHING })
    try {
      const response = await refreshReportChart(dispatch, cardId)
      if (!mountedRef.current) {
        return
      }
      setCardState(cardId, resultToCardData(response))
      if (response.refresh_error) {
        toast.error("Couldn't refresh this chart. Showing the last one that loaded.")
      }
    } catch (error) {
      if (!mountedRef.current) {
        return
      }
      if (error?.status === TOO_MANY_REQUESTS) {
        // Refused inside the cooldown (e.g. a refresh from another tab, or one
        // that failed): hold the button until the backend says it can run.
        const retryAfterSeconds =
          error.data?.detail?.retry_after_seconds ?? REFRESH_COOLDOWN_SECONDS
        setCardState(cardId, {
          status: SLOT_STATUSES.READY,
          nextRefreshAt: new Date(Date.now() + retryAfterSeconds * 1000).toISOString(),
        })
        toast.error('This chart was refreshed recently. Try again in a few minutes.', {
          cause: error,
        })
        return
      }
      setCardState(cardId, { status: SLOT_STATUSES.READY })
      toast.error("Couldn't refresh this chart.", { cause: error })
    }
  }

  const handleAsk = (cardId) => {
    history.push(`/kodi?${queryString.stringify({ [REPORT_CHART_QUERY_PARAM]: cardId })}`)
  }

  const handleCloseModal = () => {
    if (savingCardId) {
      return
    }
    setEditingCardId(null)
  }

  const handleSaveTitle = async (newTitle) => {
    const cardId = editingCardId
    if (!cardId) {
      return
    }
    setSavingCardId(cardId)
    try {
      const updated = await renameReportChart(dispatch, cardId, newTitle)
      setCards((previous) => {
        return previous.map((card) => {
          if (card.card_id === cardId) {
            return updated
          }
          return card
        })
      })
      setEditingCardId(null)
    } catch (error) {
      // Keep the modal open so the user can retry without re-typing.
      toast.error('Could not rename chart', { cause: error })
    } finally {
      setSavingCardId(null)
    }
  }

  const handleCloseRemove = () => {
    if (removeBusy) {
      return
    }
    setRemovingCardId(null)
  }

  // Takes a chart off the page. Losing someone's last chart takes them out of the
  // filter in the URL and the remembered choice too, so a later visit can't bring
  // it back unseen.
  const dropCard = async (cardId) => {
    setCards((previous) => {
      return previous.filter((card) => {
        return card.card_id !== cardId
      })
    })
    // Only the removed chart's pinner can lose their last card here. The filter
    // is shared with the other tab, so anyone else in it stays.
    const pinnerId = cards.find((card) => card.card_id === cardId)?.pinned_by?.user_id
    const pinnerHasMore = cards.some((card) => {
      return card.card_id !== cardId && card.pinned_by?.user_id === pinnerId
    })
    if (!pinnedByIds.includes(pinnerId) || pinnerHasMore) {
      return
    }
    // My reports holds all the viewer's own charts, but someone else can still
    // have public ones on the other tab.
    if (pinnerId !== currentUser.user_id) {
      try {
        const publicCards = await listPublicReportCharts(dispatch)
        if (publicCards.some((card) => card.pinned_by?.user_id === pinnerId)) {
          return
        }
      } catch (error) {
        // Can't tell, so leave the filter alone.
        return
      }
      if (!mountedRef.current) {
        return
      }
    }
    handlePinnedByChange(pinnedByIds.filter((id) => id !== pinnerId))
  }

  // Resolves true once saved, so the share popover knows to close.
  const handleSaveSharing = async (card, { visibility, viewerIds }) => {
    try {
      const updated = await setReportChartVisibility(dispatch, card.card_id, visibility, viewerIds)
      // Saved after a switch to Public reports: a chart no longer shared with
      // everyone doesn't belong there.
      const offPublic =
        tabRef.current === REPORT_TABS.PUBLIC && updated.visibility !== REPORT_VISIBILITY.ORG
      setCards((previous) => {
        return previous.flatMap((item) => {
          if (item.card_id !== card.card_id) {
            return [item]
          }
          return offPublic ? [] : [updated]
        })
      })
      toast.success(sharingToast(updated))
      return true
    } catch (error) {
      // Keep the popover open so the user can retry.
      toast.error('Could not change who can see this chart')
      return false
    }
  }

  const handleConfirmRemove = async () => {
    const cardId = removingCardId
    setRemoveBusy(true)
    try {
      await deleteReportChart(dispatch, cardId)
      // Awaited so the dialog stays up, and the filter can't change, until it's settled.
      await dropCard(cardId)
      setRemovingCardId(null)
    } catch (error) {
      // Keep the dialog open so the user can retry.
      toast.error('Could not remove chart', { cause: error })
    } finally {
      setRemoveBusy(false)
    }
  }

  const editingCard = cards.find((card) => {
    return card.card_id === editingCardId
  })
  const removingCard = cards.find((card) => {
    return card.card_id === removingCardId
  })

  const tabItems = [
    { accessor: REPORT_TABS.MINE, label: 'My reports' },
    { accessor: REPORT_TABS.PUBLIC, label: 'Public reports' },
  ].map((item) => {
    return {
      ...item,
      active: tab === item.accessor,
      onClick: () => setTab(item.accessor),
      dataTestId: `kodi-reports-tab-${item.accessor}`,
    }
  })

  const renderBody = () => {
    if (listStatus === SLOT_STATUSES.ERROR) {
      return <ErrorMessage content={listError} />
    }
    // Card names + count come from a quick metadata fetch. Until it lands we
    // can't render real shells; once it's in, each card shows its own loader.
    if (isEmpty(cards)) {
      if (listStatus === SLOT_STATUSES.READY) {
        if (isPublic) {
          return (
            <NoData
              headerText="No public reports yet"
              text="Reports shared with everyone in your organization show up here."
            />
          )
        }
        return (
          <NoData
            headerText="No pinned charts yet"
            text="Ask Kodi for a chart, then pin it to save it here. Reports shared with everyone are under Public reports."
          />
        )
      }
      return <Loader centered />
    }
    return (
      <div className="equal-grid equal-grid-large">
        {visibleCards.map((card) => {
          return (
            <KodiReportCard
              key={card.card_id}
              card={card}
              cardData={cardData[card.card_id] || { status: SLOT_STATUSES.IDLE }}
              onAsk={() => handleAsk(card.card_id)}
              onEdit={() => setEditingCardId(card.card_id)}
              onRefresh={() => handleRefresh(card.card_id)}
              onDelete={() => setRemovingCardId(card.card_id)}
              onSaveSharing={(choice) => handleSaveSharing(card, choice)}
              readOnly={isPublic}
            />
          )
        })}
      </div>
    )
  }

  // Hidden until the charts name their pinners (an older backend sends none).
  const pinnedByFilter = !isEmpty(pinners) && (
    <SearchableDropdown
      isMultiSelect
      isClearable
      additionalIcon={<IconPin />}
      additionalLabel="Pinned by:"
      buttonLabel="Everyone"
      options={pinners}
      selected={activePinnedByIds}
      handleSelect={(_, selection) => {
        handlePinnedByChange(selection.map((option) => option.value))
      }}
      selectedOptionFirst={false}
      placement="bottom-end"
      inputProps={{ placeholder: 'Search users' }}
      dataTestId="kodi-reports-pinned-by"
    />
  )

  return (
    <div className="kodi-reports" data-testid="kodi-reports">
      <PageHeader title="Kodi Reports" actions={pinnedByFilter} />
      <Menu className="mb" items={tabItems} />
      {renderBody()}
      <KodiReportEditModal
        show={Boolean(editingCard)}
        initialTitle={editingCard?.title}
        onClose={handleCloseModal}
        onSave={handleSaveTitle}
        saving={Boolean(savingCardId)}
      />
      <ConfirmModal
        show={Boolean(removingCard)}
        onClose={handleCloseRemove}
        title="Remove chart?"
        confirmLabel="Remove"
        busyLabel="Removing…"
        busy={removeBusy}
        destructive
        onConfirm={handleConfirmRemove}
        testIdPrefix="kodi-report-remove"
      >
        {/* Say whose view it leaves. */}
        <p>
          &ldquo;{removingCard?.title || 'Untitled chart'}&rdquo; {removalMessage(removingCard)}
        </p>
      </ConfirmModal>
    </div>
  )
}

export const KodiReportsPage = withLDConsumer()(KodiReportsPageComponent)
